<template>
	<div class="media-list">
		<ui-button
			v-if="hasFilters"
			class="media-list__filters__reset-button"
			floating
			icon="fal fa-times"
			primary
			tooltip="Reset all filters"
			@click="resetFilters"
		/>
		<div
			v-if="mediaList.length > 0"
			class="media-list__filters ui-elevation-1 panel"
		>
			<ui-input
				v-model="textFilter"
				clearable
				label="Filter text"
			/>
			<ui-datepicker
				ref="startDatepicker"
				v-model="selectedStartDate"
				class="media-list__filters__date"
				close-on-select
				label="From"
			/>
			<ui-datepicker
				ref="endDatepicker"
				v-model="selectedEndDate"
				class="media-list__filters__date"
				close-on-select
				label="To"
			/>
			<div class="media-list__filters__sort">
				<ui-select
					v-model="sortBy"
					class="media-list__filters__sort__select"
					label="Sort by"
				>
					<ui-select-item
						label="Date"
						value="created"
					/>
					<ui-select-item
						label="Duration"
						value="duration"
					/>
					<ui-select-item
						label="Size"
						value="size"
					/>
				</ui-select>
				<ui-button
					:icon="`fal ${sortDirection === 'asc' ? 'fa-long-arrow-down' : 'fa-long-arrow-up'}`"
					tooltip="Sort direction"
					@click="sortDirection === 'asc' ? sortDirection = 'desc' : sortDirection = 'asc';"
				/>
			</div>
			<ui-select
				v-model="selectedTagList"
				:disabled="tagList.length === 0"
				class="media-list__filters__tag-list"
				label="Tags"
				multiple
			>
				<ui-select-item
					v-for="tag in tagList"
					:key="tag"
					:label="tag"
					:value="tag"
					@click.native="mediaItemsChunkLimit = mediaItemsChunkSize"
				/>
			</ui-select>
			<ui-select
				v-model="selectedParticipantList"
				:disabled="participantList.length === 0"
				class="media-list__filters__participant-list"
				label="Participants"
				multiple
			>
				<ui-select-item
					v-for="participant in participantList"
					:key="participant"
					:label="participant"
					:value="participant"
					@click.native="mediaItemsChunkLimit = mediaItemsChunkSize"
				/>
			</ui-select>
		</div>

		<ui-button
			v-if="selectedItems.length > 0"
			class="media-list__action-button media-list__action-button--delete"
			floating
			icon="fal fa-trash"
			primary
			tooltip="Delete selected videos"
			@click="$refs.confirmBatchDeleteDialog.open"
		/>

		<ui-button
			v-if="selectedItems.length > 0"
			class="media-list__action-button media-list__action-button--deselect"
			floating
			icon="fal fa-check"
			primary
			tooltip="Deselect all videos"
			@click="deselectAllMedia"
		/>

		<ui-empty-state
			v-if="initialized && !isFetching && mediaList.length === 0"
			description="Recorded videos will show in this list"
			icon="fas fa-images"
			label="No recorded videos"
		/>

		<div class="media-list__items">
			<media-list-item
				v-for="(mediaItem, index) in chunkedMediaList"
				:ref="mediaItem.media_id"
				:key="mediaItem.media_id"
				:media-item="mediaItem"
				:recorder-api-url="recorderApiUrl"
				@confirmDeleteMedia="confirmDeleteMedia"
				@playMedia="playMedia"
				@remove="mediaList.splice(index, 1)"
				@shareMedia="shareMedia"
				@trimMedia="trimMedia"
			/>
		</div>

		<div v-if="!initialized && isFetching" class="media-list__loading">
			<ui-progress
				type="dots"
			/>
		</div>

		<ui-dialog
			ref="confirmBatchDeleteDialog"
			:title="`Permanently delete selected ${$options.filters.pluralize(selectedItems.length, 'video')}?`"
			closable
		>
			<p>You are about to delete {{ selectedItems.length }} selected {{ selectedItems.length | pluralize('video') }}, this action is permanent and cannot be undone.</p>
			<ui-dialog-footer>
				<ui-button
					text="Cancel"
					tooltip="Abort deletion"
					@click="$refs.confirmBatchDeleteDialog.close()"
				/>
				<ui-button
					:tooltip="`Permanently delete selected ${$options.filters.pluralize(selectedItems.length, 'video')}`"
					secondary
					text="Delete"
					@click="batchDeleteMedia"
				/>
			</ui-dialog-footer>
		</ui-dialog>

		<ui-dialog
			ref="confirmDeleteDialog"
			closable
			title="Permanently delete video?"
		>
			<p>You are about to delete the video <strong>{{ deleteMediaItem ? deleteMediaItem.comment || deleteMediaItem.title : "" }}</strong>, this action is permanent and cannot be undone.</p>
			<ui-dialog-footer>
				<ui-button
					text="Cancel"
					tooltip="Abort deletion"
					@click="$refs.confirmDeleteDialog.close()"
				/>
				<ui-button
					secondary
					text="Delete"
					tooltip="Permanently delete video"
					@click="deleteMedia"
				/>
			</ui-dialog-footer>
		</ui-dialog>
		<editor-dialog ref="editorDialog" />
		<share-dialog ref="shareDialog" />
		<web-player-dialog
			ref="webPlayerDialog"
			@trimMedia="trimMedia"
		/>
	</div>
</template>

<script>
import EditorDialog from "EditorDialog";
import MediaListItem from "ListItem";
import ShareDialog from "ShareDialog";
import UiButton from "lib/components/ui/Button";
import UiDatepicker from "lib/components/ui/Datepicker";
import UiDialog from "lib/components/ui/Dialog";
import UiDialogFooter from "lib/components/ui/DialogFooter";
import UiEmptyState from "lib/components/ui/EmptyState";
import UiInput from "lib/components/ui/Input";
import UiProgress from "lib/components/ui/Progress";
import UiSelect from "lib/components/ui/Select";
import UiSelectItem from "lib/components/ui/SelectItem";
import WebPlayerDialog from "WebPlayerDialog";
import alert from "lib/modules/Alert";
import http from "lib/modules/Http";

import endOfDay from "date-fns/endOfDay";
import isUndefined from "lodash/isUndefined";
import orderBy from "lodash/orderBy";
import sortBy from "lodash/sortBy";
import sortedUniq from "lodash/sortedUniq";
import startOfDay from "date-fns/startOfDay";
import throttle from "lodash/throttle";

export default {
	name: "MediaList",
	components: {
		EditorDialog,
		MediaListItem,
		ShareDialog,
		UiButton,
		UiDatepicker,
		UiDialog,
		UiDialogFooter,
		UiEmptyState,
		UiInput,
		UiProgress,
		UiSelect,
		UiSelectItem,
		WebPlayerDialog
	},
	props: {
		recorderApiUrl: {
			type: String,
			required: true
		},
		recorderId: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			deleteMediaItem: null,
			initialized: false,
			isFetching: false,
			mediaItemsChunkLimit: 0,
			mediaList: [],
			participantList: [],
			selectedEndDate: null, // date object
			selectedParticipantList: [],
			selectedStartDate: null, // date object
			selectedTagList: [],
			sortBy: "created",
			sortDirection: "desc",
			tagList: [],
			textFilter: ""
		};
	},
	computed: {
		hasFilters() {
			return this.textFilter !== "" ||
				this.selectedStartDate ||
				this.selectedEndDate ||
				this.selectedTagList.length > 0 ||
				this.selectedParticipantList.length > 0;
		},
		processedMediaList() {
			if (this.mediaList.length === 0) // computed props will run initially, so just return empty list
				return [];

			let mediaList = this.mediaList.slice(0); // clone this.mediaList array

			// filter
			if (
				this.textFilter !== "" ||
				this.selectedStartDate ||
				this.selectedEndDate ||
				this.selectedTagList.length > 0 ||
				this.selectedParticipantList.length > 0
			)
				mediaList = this.mediaList.filter((mediaItem) => {
					const textFilter = this.textFilter.toLowerCase();
					const title = mediaItem.title.toLowerCase();
					const comment = mediaItem.comment === null ? "" : mediaItem.comment.toLowerCase();

					if (this.textFilter !== "" && title.indexOf(textFilter) === -1 && comment.indexOf(textFilter) === -1)
						return false;

					if (this.selectedStartDate && mediaItem.created < Math.trunc(startOfDay(this.selectedStartDate) / 1000))
						return false;

					if (this.selectedEndDate && mediaItem.created > Math.trunc(endOfDay(this.selectedEndDate) / 1000))
						return false;

					const matchTag = this.selectedTagList.some((tag) => {
						return mediaItem.tags.includes(tag);
					});

					if (this.selectedTagList.length > 0 && !matchTag)
						return false;

					const matchParticipant = this.selectedParticipantList.some((participant) => {
						return mediaItem.participants.includes(participant);
					});

					if (this.selectedParticipantList.length > 0 && !matchParticipant)
						return false;

					return true;
				});

			// sort
			if (this.sortBy)
				mediaList = orderBy(mediaList, this.sortBy, this.sortDirection);

			return mediaList;
		},
		chunkedMediaList() {
			const mediaList = this.processedMediaList;

			return mediaList.slice(0, Math.min(this.mediaItemsChunkLimit, mediaList.length));
		},
		selectedItems() {
			return this.mediaList.filter((item) => item._selected);
		}
	},
	watch: {
		textFilter() {
			this.mediaItemsChunkLimit = this.mediaItemsChunkSize;
		},
		selectedStartDate() {
			this.mediaItemsChunkLimit = this.mediaItemsChunkSize;
		},
		selectedEndDate() {
			this.mediaItemsChunkLimit = this.mediaItemsChunkSize;
		},
		sortBy() {
			this.mediaItemsChunkLimit = this.mediaItemsChunkSize;
		},
		sortDirection() {
			this.mediaItemsChunkLimit = this.mediaItemsChunkSize;
		}
	},
	created() {
		this.getMediaList();
	},
	mounted() {
		window.addEventListener("resize", throttle(this.updateChunkSize, 100));
		window.addEventListener("scroll", throttle(this.updateChunkLimit, 100));

		this.updateChunkSize();
		this.mediaItemsChunkLimit = this.mediaItemsChunkSize;
	},
	beforeDestroy() {
		window.removeEventListener("resize", throttle(this.updateChunkSize, 100));
		window.removeEventListener("scroll", throttle(this.updateChunkLimit, 100));
	},
	methods: {
		batchDeleteMedia() {
			this.$refs.confirmBatchDeleteDialog.close();

			this.selectedItems.forEach((mediaItem) => this.deleteMediaById(mediaItem.media_id));
		},
		confirmDeleteMedia(mediaItem) {
			this.deleteMediaItem = mediaItem;
			this.$refs.confirmDeleteDialog.open();
		},
		deleteMedia() {
			this.$refs.confirmDeleteDialog.close();
			this.deleteMediaById(this.deleteMediaItem.media_id);
			this.deleteMediaItem = null;
		},
		deleteMediaById(mediaItemId) {
			if (!isUndefined(this.$refs[mediaItemId][0]))
				this.$refs[mediaItemId][0].deleteAnimationStart();

			http.delete(`${this.recorderApiUrl}/media/${mediaItemId}`)
			.catch((error) => {
				if (!isUndefined(this.$refs[mediaItemId][0]))
					this.$refs[mediaItemId][0].deleteAnimationEnd();

				this.getMediaList(); // fetch medialist to re-insert item if delete fails
				alert.error("Unable to delete media", error.response);
			});
		},
		deselectAllMedia() {
			this.selectedItems.forEach((item) => {
				item._selected = false;
			});
		},
		getMediaList() {
			this.isFetching = true;

			http.get(`${this.recorderApiUrl}/recorder/${this.recorderId}/medialist`)
			.then((response) => {
				if (typeof this.mediaListMap === "undefined") {
					this.mediaListMap = {};

					for (let i = 0; i < response.data.length; i++) {
						response.data[i]._selected = false;
						this.mediaListMap[response.data[i].media_id] = response.data[i];
					}
				}
				else {
					const mediaListMap = {};

					for (let i = 0; i < response.data.length; i++) {
						if (typeof this.mediaListMap[response.data[i].media_id] === "undefined") {
							response.data[i]._new = true;
							response.data[i]._selected = false;
						}
						else {
							response.data[i]._selected = this.mediaListMap[response.data[i].media_id]._selected;
							response.data[i].secid = this.mediaListMap[response.data[i].media_id].secid;
						}

						mediaListMap[response.data[i].media_id] = response.data[i];
					}

					this.mediaListMap = mediaListMap;
				}

				this.mediaList = response.data;
				this.updateTagAndParticipantList(this.mediaList);
				this.initialized = true;
			})
			.catch((error) => {
				alert.error("Unable to get media list", error.response);
			})
			.finally(() => {
				this.isFetching = false;
			});
		},
		playMedia(mediaItem, recorderApiUrl) {
			this.$refs.webPlayerDialog.playMedia(mediaItem, recorderApiUrl);
		},
		resetFilters() {
			this.textFilter = "";
			this.$refs.startDatepicker.clear();
			this.$refs.endDatepicker.clear();
			this.selectedTagList = [];
			this.selectedParticipantList = [];
		},
		shareMedia(mediaItem) {
			this.$refs.shareDialog.open(mediaItem);
		},
		trimMedia(mediaItem, recorderApiUrl, pos = 0) {
			this.$refs.editorDialog.trimMedia(mediaItem, recorderApiUrl, pos);
		},
		updateChunkLimit() {
			const scrollHeight = document.documentElement.scrollHeight;
			const scrollPos = document.documentElement.scrollTop + window.innerHeight;

			if (scrollPos > scrollHeight / 2 && this.mediaItemsChunkLimit < this.processedMediaList.length)
				this.mediaItemsChunkLimit += this.mediaItemsChunkSize;
		},
		updateChunkSize() {
			const viewArea = window.innerWidth * window.innerHeight;
			const itemArea = 260 * 210;

			this.mediaItemsChunkSize = Math.ceil(viewArea / itemArea) * 2;

			this.updateChunkLimit();
		},
		updateTagAndParticipantList(mediaList) {
			this.tagList = [];
			this.participantList = [];

			mediaList.forEach((mediaItem) => {
				this.tagList = sortedUniq(sortBy(this.tagList.concat(mediaItem.tags)));
				this.participantList = sortedUniq(sortBy(this.participantList.concat(mediaItem.participants)));
			});

			// remove selected items that no longer exist in participants/tags lists
			this.selectedTagList = this.selectedTagList.filter((item) => this.tagList.includes(item));
			this.selectedParticipantList = this.selectedParticipantList.filter((item) => this.participantList.includes(item));
		}
	}
};
</script>

<style lang="scss" scoped>
.media-list {
	.media-list__filters__reset-button {
		position: relative;
		top: -28px;
		right: -34px;
		margin-left: -62px;
		float: right;
	}
	.media-list__items {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
		grid-gap: 16px;
		justify-items: center;
		.media-list-item {
			align-self: start;
			width: 100%;
			max-width: 640px;
		}
	}
	.media-list__filters {
		margin-bottom: 15px;
		padding: 8px 15px;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
		grid-auto-rows: 64px;
		column-gap: 16px;
		min-width: 260px;
		.media-list__filters__date {
			min-width: 160px;
		}
		.media-list__filters__sort {
			display: flex;
			.media-list__filters__sort__select {
				min-width: 190px;
			}
			.ui-button {
				align-self: center;
				margin: 0;
			}
		}
		@media (min-width: 566px) {
			grid-template-columns: repeat(auto-fill, minmax(245px, 1fr));
		}
		@media (min-width: 857px) {
			grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
		}
		@media (min-width: 872px) {
			grid-template-columns: repeat(3, 1fr);
		}
		@media (min-width: 1148px) {
			grid-template-columns: repeat(4, minmax(252px, 1fr));
		}
		@media (min-width: 1424px) {
			grid-template-columns: auto;
			> div {
				grid-row: 1;
			}
		}
		.media-list__filters__tag-list,
		.media-list__filters__participant-list {
			grid-column: auto;
			@media (min-width: 1148px) and (max-width: 1424px) {
				grid-column: span 2;
			}
		}
	}
	.media-list__action-button {
		position: fixed;
		left: 16px;
		bottom: 86px;
		margin: 0;
		&.media-list__action-button--deselect {
			bottom: 16px;
		}
	}
}

// IE11 specific, using flex due to poor grid support
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
	.media-list {
		.media-list__filters {
			display: flex;
			flex-wrap: wrap;
			padding: 8px;
			>* {
				flex-basis: 245px; // media-list-item width - (margin x 2)
				flex-grow: 1;
				align-self: flex-start;
				margin: 8px;
				// 3 columns
				@media (min-width: 876px) {
					flex-basis: 251px;
				}
				@media (min-width: 892px) {
					flex-basis: 250px;
				}
				// 4 columns
				@media (min-width: 1156px) {
					flex-basis: 253px;
				}
			}
			.media-list__filters__sort {
				margin-top: 4px;
				width: 224px;
				min-width: 224px;
				.md-field {
					margin-bottom: 0;
				}
			}
			.media-list__filters__tag-list,
			.media-list__filters__participant-list {
				@media (min-width: 1354px) and (max-width: 1722px) {
					flex-basis: 614px;
				}
			}
		}
		.media-list__items {
			display: flex;
			flex-wrap: wrap;
			margin: 8px -8px 16px -8px;
			.media-list-item {
				flex-grow: 1;
				align-self: flex-start;
				margin: 8px;
				width: 261px;
				min-width: 261px;
			}
		}
	}
}
</style>
